import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  Clock,
  Flame,
  History,
  RefreshCw,
  Timer,
  UserX,
} from 'lucide-react';
import { Badge, Button, Card, EmptyState, PageHeader, ProgressBar, SegmentedControl, Spinner, StatCard } from '../../Components/ui';
import { formatServerDateTime } from '../../utils/serverDate';
import { DailyBars, HourHeatmap } from './ActivityCharts';
import { fetchLearnerReport } from './activityApi';
import {
  EMPTY,
  episodeType,
  formatDay,
  formatDuration,
  formatInactiveDays,
  formatVideoTime,
  lessonCode,
  lessonStatus,
  toMinutes,
} from './activityFormat';
import { DAY_OPTIONS, useReport } from './useReport';
import './LearningActivity.css';

const trackingNote = (since) =>
  since ? `Kuzatuv ${formatDay(since, { withYear: true })} dan beri` : "Kuzatuv ma'lumoti hali yo'q";

function CurrentState({ summary }) {
  const current = summary.current_lesson;
  return (
    <Card title="Qayerga keldi" subtitle="Joriy dars — o'quvchi ochgan eng oxirgi dars">
      <div className="ui-la-current">
        <p className="ui-la-current__lesson">
          {current ? `${current.chapter_order}-bob, ${current.lesson_order}-dars: ${current.title}` : EMPTY}
        </p>
        <ProgressBar value={summary.progress} size="md" showValue label="Kurs progressi" />
        <p className="ui-la-current__meta">
          Ko'rib tugatilgan darslar (video 90%+): <b>{summary.lessons_completed} / {summary.lessons_total}</b>
        </p>
      </div>
    </Card>
  );
}

function DailyCard({ daily }) {
  const rows = daily.map((row) => ({ ...row, minutes: toMinutes(row.seconds) }));
  return (
    <Card title="Kunlik o'qish vaqti" subtitle="Video haqiqatda ko'rilgan vaqt, daqiqa (Toshkent vaqti)">
      <DailyBars
        data={rows}
        valueKey="minutes"
        label="Kunlik o'qish vaqti"
        tooltip={(row) => (
          <>
            <b>{formatDuration(row.seconds)}</b>
            <span>{formatDay(row.date, { withYear: true })}</span>
          </>
        )}
      />
      <details className="ui-la-details">
        <summary>Jadval ko'rinishi</summary>
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Sana</th>
                <th className="ui-table-cell--numeric">Vaqt</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((row) => (
                <tr key={row.date}>
                  <td>{formatDay(row.date, { withYear: true })}</td>
                  <td className="ui-table-cell--numeric">{formatDuration(row.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Card>
  );
}

function LessonsCard({ lessons }) {
  return (
    <Card title="Darslar" subtitle="Har bir dars videosi qancha ko'rilgani va dars paytidagi harakatlar" padding="none">
      <div className="ui-table-wrap ui-la-table">
        <table className="ui-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Dars</th>
              <th>Ko'rilgan</th>
              <th className="ui-table-cell--numeric">Vaqt</th>
              <th className="ui-table-cell--numeric" title="Orqaga qaytib qayta ko'rishlar">Qayta ko'rish</th>
              <th className="ui-table-cell--numeric" title="Boshqa tabga o'tgani uchun video to'xtagan">Tab pauza</th>
              <th className="ui-table-cell--numeric">PDF</th>
              <th className="ui-table-cell--numeric">AI savol</th>
              <th className="ui-table-cell--numeric" title="Kamera nazorati: yuz kadrda bo'lmagani uchun video to'xtagan">Turib ketdi</th>
              <th className="ui-table-cell--numeric" title="Kamera nazorati: boshqa tomonga qaragani uchun video to'xtagan">Chalg'idi</th>
              <th>Holat</th>
            </tr>
          </thead>
          <tbody>
            {lessons.map((row) => {
              const status = lessonStatus(row);
              return (
                <tr key={row.lesson_id}>
                  <td className="ui-la-table__code">{lessonCode(row)}</td>
                  <td>
                    <span className="ui-la-table__title" title={row.title}>{row.title}</span>
                  </td>
                  <td>
                    <ProgressBar
                      value={row.watched_percent}
                      showValue
                      tone={row.completed_at ? 'success' : 'info'}
                      label={`${row.title}: ko'rilgan`}
                      className="ui-la-table__progress"
                    />
                  </td>
                  <td className="ui-table-cell--numeric">{row.watched_sec ? formatDuration(row.watched_sec) : EMPTY}</td>
                  <td className="ui-table-cell--numeric">{row.rewinds || EMPTY}</td>
                  <td className="ui-table-cell--numeric">{row.tab_hidden || EMPTY}</td>
                  <td className="ui-table-cell--numeric">{row.pdf_opens || EMPTY}</td>
                  <td className="ui-table-cell--numeric">{row.ai_questions || EMPTY}</td>
                  <td
                    className="ui-table-cell--numeric"
                    title={row.away_count ? `Jami ${formatDuration(row.away_seconds)}` : undefined}
                  >
                    {row.away_count || EMPTY}
                  </td>
                  <td className="ui-table-cell--numeric">{row.look_away_count || EMPTY}</td>
                  <td>
                    <Badge variant={status.variant} size="sm">{status.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Kamera nazorati (MediaPipe) video to'xtatgan holatlar: qaysi dars, videoning qaysi joyi, qancha vaqt
function AttentionCard({ attention, days }) {
  const { episodes } = attention;
  return (
    <Card
      title="Diqqat (kamera nazorati)"
      subtitle={`Oxirgi ${days} kun: turib ketgani yoki boshqa tomonga qaragani uchun video to'xtagan holatlar`}
      padding={episodes.length ? 'none' : 'md'}
    >
      <dl className={`ui-la-attn${episodes.length ? ' ui-la-attn--padded' : ''}`}>
        <div className="ui-la-attn__item">
          <dt>Turib ketdi</dt>
          <dd>{attention.away_count} marta · {formatDuration(attention.away_seconds)}</dd>
        </div>
        <div className="ui-la-attn__item">
          <dt>Boshqa tomonga qaradi</dt>
          <dd>{attention.look_away_count} marta · {formatDuration(attention.look_away_seconds)}</dd>
        </div>
        <div className="ui-la-attn__item">
          <dt>Kamera ishlamagan darslar</dt>
          <dd>{attention.camera_off_sessions}</dd>
        </div>
      </dl>
      {episodes.length === 0 ? (
        <p className="ui-la-muted">Bu davrda kamera nazorati videoni to'xtatmagan</p>
      ) : (
        <div className="ui-table-wrap ui-la-table">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Vaqt</th>
                <th>Dars</th>
                <th>Videodagi joy</th>
                <th>Holat</th>
                <th className="ui-table-cell--numeric">Davomiyligi</th>
              </tr>
            </thead>
            <tbody>
              {episodes.map((episode, index) => {
                const kind = episodeType(episode.type);
                return (
                  <tr key={`${episode.created_at}-${index}`}>
                    <td>{formatServerDateTime(episode.created_at) || EMPTY}</td>
                    <td>
                      <span className="ui-la-table__title" title={episode.title}>
                        {lessonCode(episode)} · {episode.title}
                      </span>
                    </td>
                    <td>{formatVideoTime(episode.position_sec)}</td>
                    <td>
                      <Badge variant={kind.variant} size="sm">{kind.label}</Badge>
                    </td>
                    <td className="ui-table-cell--numeric">{formatDuration(episode.seconds)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ExamsCard({ exams }) {
  return (
    <Card title="Imtihon urinishlari" padding={exams.length ? 'none' : 'md'}>
      {exams.length === 0 ? (
        <p className="ui-la-muted">Hali imtihon topshirilmagan</p>
      ) : (
        <div className="ui-table-wrap ui-la-table">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Bob</th>
                <th>Sana</th>
                <th className="ui-table-cell--numeric">Ball</th>
                <th>Natija</th>
              </tr>
            </thead>
            <tbody>
              {exams.map((exam) => (
                <tr key={exam.id}>
                  <td>{exam.chapter_order ? `${exam.chapter_order}-bob. ${exam.chapter_title}` : EMPTY}</td>
                  <td>{formatServerDateTime(exam.date_time) || EMPTY}</td>
                  <td className="ui-table-cell--numeric">{exam.total_score ?? EMPTY}</td>
                  <td>
                    <Badge variant={exam.passed ? 'success' : 'danger'} size="sm">
                      {exam.passed ? "O'tdi" : "O'tmadi"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// /learning-activity?learnerId=… — talabaning bitta kursdagi o'qish faolligi (learnerId — enrollment id)
const StudentActivity = () => {
  const [searchParams] = useSearchParams();
  const learnerId = searchParams.get('learnerId');
  const [days, setDays] = useState(30);
  const [reload, setReload] = useState(0);
  const { status, data } = useReport(fetchLearnerReport, learnerId, days, reload);

  const breadcrumb = [{ label: 'Studentlar', to: '/students' }, { label: "O'qish faolligi" }];

  if (!learnerId) {
    return (
      <div className="ui-la">
        <PageHeader title="O'qish faolligi" breadcrumb={breadcrumb} />
        <Card padding="none">
          <EmptyState title="Talaba tanlanmagan" description="Studentlar sahifasidan talaba va kursni tanlang" />
        </Card>
      </div>
    );
  }

  const summary = data?.summary;
  const title = data ? `${data.student.first_name} ${data.student.last_name}` : "O'qish faolligi";
  const subtitle = data ? `${data.course.title} · ${trackingNote(data.tracking_since)}` : undefined;

  return (
    <div className="ui-la">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        actions={
          <>
            <SegmentedControl aria-label="Davr" size="sm" options={DAY_OPTIONS} value={days} onChange={setDays} />
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw />} onClick={() => setReload((x) => x + 1)}>
              Yangilash
            </Button>
          </>
        }
      />

      {!data && status === 'loading' && (
        <div className="ui-la-loading">
          <Spinner size="lg" label="Yuklanmoqda" />
        </div>
      )}

      {!data && status === 'error' && (
        <Card padding="none">
          <EmptyState
            title="Ma'lumotni yuklab bo'lmadi"
            action={<Button variant="secondary" onClick={() => setReload((x) => x + 1)}>Qayta urinish</Button>}
          />
        </Card>
      )}

      {data && (
        <div className={`ui-la__body${status === 'loading' ? ' ui-la__body--refreshing' : ''}`}>
          <div className="ui-la__kpis">
            <StatCard icon={<Clock />} tone="info" label="Bugun" value={formatDuration(summary.today_seconds)} />
            <StatCard icon={<CalendarCheck />} tone="info" label="Oxirgi 7 kun" value={formatDuration(summary.last_7_days_seconds)} />
            <StatCard icon={<Timer />} tone="neutral" label="Jami ko'rilgan" value={formatDuration(summary.total_seconds)} />
            <StatCard icon={<Activity />} tone="success" label="Faol kunlar (1 daq+)" value={`${summary.active_days} kun`} />
            <StatCard icon={<Flame />} tone="warning" label="Ketma-ket kunlar" value={`${summary.streak_days} kun`} />
            <StatCard icon={<History />} tone="neutral" label="Faol kunda o'rtacha" value={formatDuration(summary.avg_active_day_seconds)} />
            <StatCard
              icon={<UserX />}
              tone={summary.inactive_days === null || summary.inactive_days >= 7 ? 'brand' : 'neutral'}
              label="So'nggi faollik"
              value={formatInactiveDays(summary.inactive_days)}
              title={formatServerDateTime(summary.last_activity_at) || undefined}
            />
            <StatCard
              icon={summary.forecast_finish_date ? <CalendarClock /> : <CheckCircle2 />}
              tone="neutral"
              label="Shu tezlikda tugatadi"
              value={summary.forecast_finish_date ? formatDay(summary.forecast_finish_date, { withYear: true }) : EMPTY}
              title="Oxirgi 14 kundagi o'rtacha tezlik va qolgan videolar davomiyligi bo'yicha"
            />
          </div>

          <div className="ui-la__grid-2">
            <CurrentState summary={summary} />
            <Card title="Qaysi kun va soatlarda o'qiydi" subtitle={`Oxirgi ${days} kun, Toshkent vaqti`}>
              <HourHeatmap cells={data.heatmap} />
            </Card>
          </div>

          <DailyCard daily={data.daily} />
          <AttentionCard attention={data.attention} days={days} />
          <LessonsCard lessons={data.lessons} />
          <ExamsCard exams={data.exams} />
        </div>
      )}
    </div>
  );
};

export default StudentActivity;
